import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { sitesAPI, uploadAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function CreateSitePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    domain: '',
    brand_name: '',
    template_id: 'entry_site',
    affiliate_url: '',
    fallback_affiliate_url: '',
    main_offer: '',
    main_keywords: '',
    secondary_keywords: '',
    payment_methods: 'Papara, Havale/EFT, Kripto',
    language: 'tr',
    country: 'TR',
    logo_url: '',
    brand_colors: {
      primary: '#1a237e',
      secondary: '#1565c0',
      accent: '#ffd700',
      background: '#0d1117',
      text: '#ffffff',
    },
    sports_config: null,
    create_email_infra: true,
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      brand_colors: { ...prev.brand_colors, [field]: value },
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Gecersiz dosya tipi. PNG, JPG, WebP, SVG veya GIF yukleyin.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan buyuk olamaz.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setLogoUploading(true);
    try {
      const { data } = await uploadAPI.logo(file);
      handleChange('logo_url', data.logo_url);
      toast.success('Logo yuklendi!');
    } catch (err) {
      toast.error('Logo yuklenemedi');
      setLogoPreview(null);
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    handleChange('logo_url', '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        main_keywords: form.main_keywords.split(',').map((k) => k.trim()).filter(Boolean),
        secondary_keywords: form.secondary_keywords.split(',').map((k) => k.trim()).filter(Boolean),
        payment_methods: form.payment_methods.split(',').map((k) => k.trim()).filter(Boolean),
      };
      const { data } = await sitesAPI.create(payload);
      // Auto-create email infra if checkbox checked
      if (form.create_email_infra) {
        try {
          await api.post(`/sites/${data.id}/email-infra/create`);
          toast.success('Site + Email infra olusturuldu!');
        } catch(e) {
          toast.success('Site olusturuldu! (Email infra hata verdi)');
        }
      } else {
        toast.success('Site olusturuldu!');
      }
      navigate(`/sites/${data.id}/detail`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Site olusturulamadi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Button variant="ghost" onClick={() => navigate('/sites')} className="gap-2 mb-4 text-muted-foreground">
          <ArrowLeft className="w-4 h-4" /> Geri
        </Button>

        <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk'] mb-1">Yeni Site Olustur</h1>
        <p className="text-sm text-muted-foreground">Template secin ve affiliate sitesi bilgilerini girin</p>
      </motion.div>

      {/* Template Selection */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: 0.03 }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div
          onClick={() => { handleChange('template_id', 'entry_site'); handleChange('sports_config', null); }}
          className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-150 ${form.template_id === 'entry_site' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border/70 hover:border-border'}`}
          data-testid="template-entry-site"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center text-[hsl(var(--primary))] text-lg font-bold">G</div>
            <div>
              <h3 className="font-semibold text-sm">Giris Sitesi</h3>
              <p className="text-[11px] text-muted-foreground">13 sayfa</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Marka giris, bonus, guvenilir mi, mobil giris, odeme sayfalari.</p>
        </div>
        <div
          onClick={() => { handleChange('template_id', 'entry_match_scores'); handleChange('sports_config', { sports: ['football', 'basketball'], data_mode: 'static_demo', show_live_scores: true, show_finished_matches: true, show_fixtures: true }); }}
          className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-150 ${form.template_id === 'entry_match_scores' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border/70 hover:border-border'}`}
          data-testid="template-entry-match-scores"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 text-lg font-bold">S</div>
            <div>
              <h3 className="font-semibold text-sm">Giris + Mac Skorlari</h3>
              <p className="text-[11px] text-muted-foreground">16 sayfa</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Giris sitesi + canli skor, mac sonuclari, lig sayfalari ve spor SEO icerikleri.</p>
        </div>
        <div
          onClick={() => { handleChange('template_id', 'media_affiliate_v3'); handleChange('sports_config', null); }}
          className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-150 ${form.template_id === 'media_affiliate_v3' ? 'border-[#B7FF2A] bg-[#B7FF2A]/5' : 'border-border/70 hover:border-border'}`}
          data-testid="template-media-affiliate-v3"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#B7FF2A]/10 flex items-center justify-center text-[#B7FF2A] text-lg font-bold">M</div>
            <div>
              <h3 className="font-semibold text-sm">Spor Medya (V3)</h3>
              <p className="text-[11px] text-muted-foreground">15 sayfa</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Premium spor medya publisher. Haber + transfer + editorial affiliate.</p>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main form */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
            className="lg:col-span-8 space-y-6"
          >
            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base">Temel Bilgiler</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Marka Adi *</Label>
                    <Input value={form.brand_name} onChange={(e) => handleChange('brand_name', e.target.value)} placeholder="Marka Adi" className="mt-1.5 bg-secondary/40 border-border/70" data-testid="create-site-name-input" required />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Domain *</Label>
                    <Input value={form.domain} onChange={(e) => handleChange('domain', e.target.value)} placeholder="orneksite.com" className="mt-1.5 bg-secondary/40 border-border/70" data-testid="create-site-domain-input" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Ana Teklif</Label>
                  <Input value={form.main_offer} onChange={(e) => handleChange('main_offer', e.target.value)} placeholder="%500 Hos Geldin Bonusu" className="mt-1.5 bg-secondary/40 border-border/70" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Affiliate URL *</Label>
                    <Input value={form.affiliate_url} onChange={(e) => handleChange('affiliate_url', e.target.value)} placeholder="https://..." className="mt-1.5 bg-secondary/40 border-border/70 font-mono text-xs" required />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Yedek Affiliate URL</Label>
                    <Input value={form.fallback_affiliate_url} onChange={(e) => handleChange('fallback_affiliate_url', e.target.value)} placeholder="https://..." className="mt-1.5 bg-secondary/40 border-border/70 font-mono text-xs" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base">SEO & Icerik</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Ana Anahtar Kelimeler (virgul ile ayirin)</Label>
                  <Textarea value={form.main_keywords} onChange={(e) => handleChange('main_keywords', e.target.value)} placeholder="site giris, guncel giris, bonus" className="mt-1.5 bg-secondary/40 border-border/70" rows={2} />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Ikincil Anahtar Kelimeler</Label>
                  <Textarea value={form.secondary_keywords} onChange={(e) => handleChange('secondary_keywords', e.target.value)} placeholder="turkiye, canli bahis, para yatirma" className="mt-1.5 bg-secondary/40 border-border/70" rows={2} />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Odeme Yontemleri (virgul ile ayirin)</Label>
                  <Input value={form.payment_methods} onChange={(e) => handleChange('payment_methods', e.target.value)} className="mt-1.5 bg-secondary/40 border-border/70" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            className="lg:col-span-4 space-y-6"
          >
            {/* Logo Upload Card */}
            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base">Marka Logosu</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {logoPreview || form.logo_url ? (
                    <div className="relative group">
                      <div className="w-full h-28 rounded-lg bg-secondary/60 border border-border/70 flex items-center justify-center overflow-hidden p-3">
                        <img
                          src={logoPreview || form.logo_url}
                          alt="Logo"
                          className="max-h-full max-w-full object-contain"
                          data-testid="logo-preview-image"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute top-2 right-2 p-1 rounded-full bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                        data-testid="remove-logo-button"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="logo-upload"
                      className="flex flex-col items-center justify-center w-full h-28 rounded-lg border-2 border-dashed border-border/70 hover:border-primary/50 bg-secondary/30 cursor-pointer transition-colors duration-150"
                      data-testid="logo-upload-area"
                    >
                      {logoUploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs text-muted-foreground">Yukleniyor...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <div className="p-2 rounded-full bg-primary/10">
                            <Upload className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-xs text-muted-foreground">Logo Yukle</span>
                          <span className="text-[10px] text-muted-foreground/60">PNG, JPG, SVG, WebP (max 5MB)</span>
                        </div>
                      )}
                    </label>
                  )}
                  <input
                    ref={fileInputRef}
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  {(logoPreview || form.logo_url) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="change-logo-button"
                    >
                      <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
                      Logo Degistir
                    </Button>
                  )}
                  <p className="text-[10px] text-muted-foreground/70">
                    Logo, siteye ve AI gorsel uretiminde kullanilacak.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base">Marka Renkleri</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {['primary', 'secondary', 'accent', 'background', 'text'].map((key) => (
                  <div key={key} className="flex items-center gap-3">
                    <input type="color" value={form.brand_colors[key]} onChange={(e) => handleColorChange(key, e.target.value)} className="w-8 h-8 rounded border-0 cursor-pointer" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground capitalize">{key === 'text' ? 'Yazi' : key === 'background' ? 'Arka Plan' : key === 'accent' ? 'Vurgu' : key === 'primary' ? 'Ana' : 'Ikincil'}</p>
                      <p className="text-xs font-mono">{form.brand_colors[key]}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardHeader><CardTitle className="text-base">Yayin Kontrol</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="text-xs text-muted-foreground space-y-2">
                  <p>Site taslak olarak olusturulacak.</p>
                  <p>Icerik uretimi icin site detay sayfasindan AI motoru calistirilabilir.</p>
                </div>
                <label className="flex items-center gap-2.5 p-2.5 rounded-lg bg-secondary/30 border border-border/50 cursor-pointer">
                  <input type="checkbox" checked={form.create_email_infra} onChange={(e) => handleChange('create_email_infra', e.target.checked)} className="rounded" />
                  <div>
                    <p className="text-xs font-medium">Email Infrastructure Olustur</p>
                    <p className="text-[10px] text-muted-foreground">mail.domain, click tracking, DNS kayitlari ve ANG Digital register</p>
                  </div>
                </label>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="create-site-submit-button"
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 press-active font-medium"
                >
                  {loading ? 'Olusturuluyor...' : 'Siteyi Olustur'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </form>
    </div>
  );
}
